import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Globe, Share2, TrendingUp, Palette, Code, ArrowUpRight } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  1: Globe,
  2: Share2,
  3: TrendingUp,
  4: Palette,
  5: Code,
};

const colorMap = {
  'mid-blue': { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30', hover: 'hover:bg-cyan-500/20' },
  'mid-purple': { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30', hover: 'hover:bg-purple-500/20' },
  'mid-orange': { bg: 'bg-orange-500/10', text: 'text-orange-400', border: 'border-orange-500/30', hover: 'hover:bg-orange-500/20' },
  'mid-pink': { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/30', hover: 'hover:bg-pink-500/20' },
  'mid-green': { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', hover: 'hover:bg-emerald-500/20' },
};

const ServicesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };

  return (
    <section id="services" className="py-24 lg:py-32 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="service-button bg-cyan-500/20 text-cyan-400 border-cyan-500/30 mb-4 inline-block">
            What We Do
          </span>
          <h2 className="section-heading text-white mt-4">
            Services That Drive
            <span className="text-cyan-400"> Results</span>
          </h2>
          <p className="body-large text-white/60 mt-4 max-w-2xl">
            From web development to full-scale digital marketing, we provide comprehensive solutions tailored to your business goals.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6"
        >
          {services.map((service) => {
            const Icon = iconMap[service.id];
            const colors = colorMap[service.color];

            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className={`group relative block rounded-lg border ${colors.border} ${colors.bg} ${colors.hover} p-8 transition-all duration-300 cursor-pointer`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className={`p-3 rounded-lg ${colors.bg} ${colors.text}`}>
                      <Icon size={28} />
                    </div>
                    <ArrowUpRight
                      size={24}
                      className={`${colors.text} opacity-0 group-hover:opacity-100 transition-opacity`}
                    />
                  </div>

                  <h3 className="card-heading text-white mb-3 group-hover:underline">{service.title}</h3>
                  <p className="body-medium text-white/60 mb-6">{service.description}</p>

                  <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs font-mono tracking-wider px-3 py-1 rounded-full bg-white/5 text-white/50"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  <div className={`mt-6 inline-flex items-center gap-2 ${colors.text} text-sm font-medium`}>
                    Learn More <ArrowUpRight size={16} />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
